// CommandSettingsModal - コマンド設定モーダル
// トップ画面と同じ専用ダイアログ（InScreenModal + BuilderModalPanel）で表示する。
// 各コマンドの ON/OFF に加えて、使用個数の上限（むせいげん / 1〜20 こ）を設定できる。

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { usePlaySound, btnDelay } from "common/utils";
import { actions } from "features/builder/slice";
import { updatePieceCommands } from "features/builder/api";
import { BuilderCommandId, CommandLimits } from "features/builder/types";
import { COMMAND_OPTIONS, COMMAND_CATEGORIES } from "features/builder/commandOptions";
import { InScreenModal, BuilderModalPanel, BuilderModalAccent } from "features/builder/elements";

// 個数上限の値域は 1〜20。「むせいげん」は limits にキーを持たないことで表す
const LIMIT_MAX = 20;

export const CommandSettingsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const dispatch = useDispatch();

  const storeCommands = useSelector((state: RootState) => state.builder.enabledCommands);
  const storeLimits = useSelector((state: RootState) => state.builder.commandLimits);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const active = useSelector((state: RootState) => state.user.active);

  // 開くたびに slice の enabledCommands / commandLimits で初期化（spec.md §5.8.7）
  const [enabledCommands, setEnabledCommands] = useState<Set<BuilderCommandId>>(
    new Set(storeCommands),
  );
  const [limits, setLimits] = useState<CommandLimits>(storeLimits);

  useEffect(() => {
    if (open) {
      setEnabledCommands(new Set(storeCommands));
      setLimits(storeLimits);
    }
  }, [open, storeCommands, storeLimits]);

  const handleClose = () => {
    play();
    btnDelay(() => onClose());
  };

  const handleToggleCommand = (commandId: BuilderCommandId) => {
    play();
    setEnabledCommands((prev) => {
      const next = new Set(prev);
      if (next.has(commandId)) {
        next.delete(commandId);
      } else {
        next.add(commandId);
      }
      return next;
    });
  };

  // 個数の増減。値域は [1..20, むせいげん]。＋で 20 の次がむせいげん、−でむせいげんの前が 20
  const handleLimitChange = (commandId: BuilderCommandId, delta: number) => () => {
    play();
    setLimits((prev) => {
      const current = prev[commandId];
      const next = { ...prev };
      if (current == null) {
        // むせいげん → − で 20 に。＋は何もしない
        if (delta < 0) next[commandId] = LIMIT_MAX;
        return next;
      }
      const value = current + delta;
      if (value > LIMIT_MAX) {
        delete next[commandId]; // 20 → ＋ でむせいげんに戻る
      } else if (value >= 1) {
        next[commandId] = value;
      }
      return next;
    });
  };

  const handleSave = () => {
    play();
    btnDelay(async () => {
      const blocks = Array.from(enabledCommands);
      // OFF にしたコマンドの上限は破棄
      const activeLimits: CommandLimits = {};
      blocks.forEach((id) => {
        if (limits[id] != null) activeLimits[id] = limits[id];
      });
      const hasLimits = Object.keys(activeLimits).length > 0;
      dispatch(actions.setCommandSettings({ commands: blocks, limits: activeLimits }));
      await updatePieceCommands({
        piece_id: pieceId,
        uid: active.uid,
        enabled_commands: blocks,
        command_limits: hasLimits ? activeLimits : null,
      });
      onClose();
    });
  };

  return (
    <InScreenModal open={open} onBackdropClose={handleClose}>
      <BuilderModalPanel
        accent={BuilderModalAccent.GREEN}
        icon="extension"
        title={I18n.t("MSG_BUILDER_COMMAND_SETTINGS_TITLE")}
        width={680}
        onClose={handleClose}
      >
        {/* カテゴリ別コマンド一覧 */}
        <div className="flex-col-el w-full gap-4 max-h-[400px] overflow-y-auto invisible-scrollbar">
          {COMMAND_CATEGORIES.map((category) => (
            <div key={category.id} className="flex-col-el items-start w-full gap-2">
              <p className="type-text-left type-md text-textcolor-black w-full">
                {I18n.t(category.labelKey)}
              </p>
              <div className="flex-col-el w-full gap-1">
                {COMMAND_OPTIONS.filter((b) => b.category === category.id).map((block) => {
                  const isEnabled = enabledCommands.has(block.id);
                  const limit = limits[block.id];
                  return (
                    <div
                      key={block.id}
                      onClick={() => handleToggleCommand(block.id)}
                      className={`flex-row-el items-center box-border w-full gap-3 px-4 py-3 rounded-xl border-solid border-2 cursor-pointer transition-all ${
                        isEnabled
                          ? `${category.bgClass} ${category.borderClass}`
                          : "bg-white border-gray2-60"
                      }`}
                    >
                      <span
                        className={`material-symbols-outlined type-md ${
                          isEnabled
                            ? `material-symbols-filled ${category.accentClass}`
                            : "text-gray2-100"
                        }`}
                      >
                        {isEnabled ? "check_box" : "check_box_outline_blank"}
                      </span>
                      {/* コマンドのアイコン。他の画面と同じ絵・同じ色にする */}
                      <span
                        className={`material-symbols-outlined type-md ${
                          isEnabled ? category.accentClass : "text-gray2-100"
                        }`}
                      >
                        {block.icon}
                      </span>
                      <p
                        className={`type-text-left type-body flex-1 ${
                          isEnabled ? "text-textcolor-black" : "text-textcolor-black/60"
                        }`}
                      >
                        {I18n.t(block.labelKey)}
                      </p>

                      {/* 使用個数の上限（ON のときだけ）。行クリックの toggle と分離する */}
                      {isEnabled && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="flex-row-el items-center gap-1 shrink-0"
                        >
                          <button
                            type="button"
                            onClick={handleLimitChange(block.id, -1)}
                            disabled={limit === 1}
                            aria-label="limit-minus"
                            className={`type-inherit-font flex-col-center w-8 h-8 rounded-full bg-white border-solid border-2 ${category.borderClass} cursor-pointer disabled:border-gray2-60 disabled:cursor-default`}
                          >
                            <span className="material-symbols-outlined type-sm text-textcolor-black">
                              remove
                            </span>
                          </button>
                          <div
                            className={`flex-col-center w-32 h-8 px-2 rounded-full bg-white border-solid border-2 ${category.borderClass}`}
                          >
                            <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                              {limit == null
                                ? I18n.t("MSG_BUILDER_COMMAND_LIMIT_UNLIMITED")
                                : I18n.t("MSG_BUILDER_COMMAND_LIMIT_COUNT", { count: limit })}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleLimitChange(block.id, 1)}
                            disabled={limit == null}
                            aria-label="limit-plus"
                            className={`type-inherit-font flex-col-center w-8 h-8 rounded-full bg-white border-solid border-2 ${category.borderClass} cursor-pointer disabled:border-gray2-60 disabled:cursor-default`}
                          >
                            <span className="material-symbols-outlined type-sm text-textcolor-black">
                              add
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* フッター：選択数 ＋ 操作ボタン */}
        <div className="flex-row-el items-center justify-between w-full">
          <span className="type-text-left type-sm text-textcolor-black/60 flex-1 min-w-0 !shrink truncate">
            {I18n.t("MSG_BUILDER_COMMAND_SELECTED_COUNT", { count: enabledCommands.size })}
          </span>
          <div className="flex-row-el flex-center gap-2 upsilon shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="type-inherit-font btn btn-blue shrink-0"
            >
              <div className="flex-row-center gap-1">
                <span className="material-symbols-outlined text-white type-md">close</span>
                <p className="type-text type-body text-white whitespace-nowrap">
                  {I18n.t("MSG_CLOSE_BTN")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="type-inherit-font btn btn-primary shrink-0"
            >
              <div className="flex-row-center gap-1">
                <span className="material-symbols-outlined text-white type-md">check</span>
                <p className="type-text type-body text-white whitespace-nowrap">
                  {I18n.t("MSG_CONFIRM_BTN")}
                </p>
              </div>
            </button>
          </div>
        </div>
      </BuilderModalPanel>
    </InScreenModal>
  );
};
